/** @format */

import {Fragment, ReactNode, useState} from 'react';

import {Trans} from 'react-i18next';

import {WidgetBlockId} from 'pages/Home/DigitalCast/models/widgets';

import {AnalyticGoalFromQuery} from 'pages/Home/DigitalCast/models/api';

import CrSelectPopover from './components/CrSelectPopover';

import {CrSettingsButton} from './components/CrSettingsButton';
import {WidgetMethodButton} from './components/WidgetMethodButton';
import WidgetStatsColumn from './components/WidgetStatsColumn';
import WidgetSwitcherButton from './components/WidgetSwitcherButton';
import getWidgetStatsData from './helpers/getWidgetStatsData';
import {
  CalcMethod,
  CrCustomGoals,
  ShowCondition,
  Widget,
  WidgetSettings,
  WidgetStat,
} from './model';
import {
  Background,
  BodyRow,
  ButtonsWrap,
  Content,
  HeadingRow,
  IconAndNameBox,
  IconBox,
  IconText,
  Root,
  StatsBox,
  StatsDivider,
  WidgetNameText,
} from './style';
import {getComputedCrLabel} from './utils';
import {ROUTES} from '../../constants/ROUTES';
import {useCompanyContext} from '../../contexts/CompanyContext';
import {WidgetGearButton} from './components/WidgetGearButton';
import {WidgetScaleButton} from './components/WidgetScaleButton';
import {ScaleType} from './components/WidgetScaleButton/types';

interface Props<V extends Record<string, unknown>> {
  data: Widget<V>;
  color: string;
  onSwitcherClick: () => void;
  onMethodClick: (newMethod: CalcMethod) => void;
  onScaleClick: (newScale: boolean) => void;
  selected: boolean;
  widgetSettings?: WidgetSettings;
  categoryLabel?: string;
  CategoryIcon?: ReactNode;
  showCondition?: ShowCondition; // Фильтр состояний виджета, по умолчанию all
  blockId?: WidgetBlockId;
  analyticGoals?: ReadonlyArray<AnalyticGoalFromQuery>;
  onCrConfirm?: (aId: string, bId: string) => void;
}

const PropertyWidget = <V extends Record<string, unknown>>({
  data,
  color,
  onSwitcherClick,
  onMethodClick,
  onScaleClick,
  selected,
  blockId,
  widgetSettings,
  categoryLabel,
  CategoryIcon,
  showCondition = 'all',
  analyticGoals,
  onCrConfirm,
}: Props<V>) => {
  const [crAnchorEl, setCrAnchorEl] = useState<HTMLElement | null>(null);

  const {currentCompany} = useCompanyContext() || {};

  if (data.hide) {
    return null;
  }

  const {label, token} = data;

  const stats: WidgetStat[] = getWidgetStatsData({
    showCondition,
    widgetSettings,
  });

  // Для CR-виджетов без статистики показываем минимальный плейсхолдер,
  // чтобы пользователь мог настроить показатели
  const isCrWidget = data.label === 'CR' || String(data.label).startsWith('CR');
  const effectiveStats: WidgetStat[] =
    stats.length > 0 ? stats : isCrWidget ? ([{heading: ''}] as WidgetStat[]) : [];
  if (!effectiveStats.length) {
    return null;
  }

  const customData = (widgetSettings?.custom as CrCustomGoals) || undefined;
  const customLabel = customData?.label;
  const computedCrLabel = getComputedCrLabel(data, customData, analyticGoals || []);

  const labelWithToken = customLabel || computedCrLabel || (token ? `${label}, ${token}` : label);

  const handleCloseCr = () => setCrAnchorEl(null);

  const handleOpenCr = (event: React.MouseEvent<HTMLElement>) => setCrAnchorEl(event.currentTarget);

  const handleScale = (newValue: ScaleType) => {
    onScaleClick(newValue === 'log');
  };

  const crOptions = [
    ...(analyticGoals || []).map(goal => ({
      id: goal.id,
      name: goal.name,
    })),
    {
      id: 'clicks',
      name: 'Клики',
    },
    {
      id: 'shows',
      name: 'Показы',
    },
  ];

  const calcMethod = widgetSettings?.calcMethod;
  const showCategory = Boolean(categoryLabel && CategoryIcon);
  const showStats = Boolean(effectiveStats.length);
  const isShowCrSettingsButton = isCrWidget && !(customData?.aGoalId && customData?.bGoalId);
  const isFunnel = blockId === 'funnel' && !isCrWidget;

  // Сравнение с none нужно для обратной совместимости с предыдущей реализацией. Не удалять!
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const showButtonSumDay = Boolean(calcMethod && calcMethod !== 'none');
  const showButtonGear = (isFunnel || isCrWidget) && Boolean(currentCompany?.id);
  const showScaleButton = widgetSettings?.isScaleChecked !== undefined;
  const showRightColumn = showButtonSumDay || showButtonGear || showScaleButton;
  const showRightColumnOnly = !showStats && showRightColumn;

  return (
    <Root>
      <Background color={color} selected={selected} />
      <Content>
        <HeadingRow>
          <IconAndNameBox selected={selected}>
            {showCategory && (
              <IconBox>
                <IconText color={color}>
                  <Trans>{categoryLabel}</Trans>
                </IconText>
                {CategoryIcon}
              </IconBox>
            )}
            <WidgetNameText color={color}>{labelWithToken}</WidgetNameText>
          </IconAndNameBox>

          <WidgetSwitcherButton color={color} onClick={onSwitcherClick} selected={selected} />
        </HeadingRow>

        <BodyRow showRightColumnOnly={showRightColumnOnly} selected={selected}>
          {showStats && (
            <StatsBox>
              {effectiveStats.map((stat, index, arr) => {
                const isLastColumn = index === arr.length - 1;
                return (
                  <Fragment key={index}>
                    <WidgetStatsColumn data={stat} />
                    {!isLastColumn && (
                      <StatsDivider color={color} hideLine={stat.dontDrawDividerAfter} />
                    )}
                  </Fragment>
                );
              })}
            </StatsBox>
          )}

          {isShowCrSettingsButton && (
            <CrSettingsButton color={color} onCrSettingsClick={handleOpenCr} disabled={!selected} />
          )}

          {showRightColumn && (
            <ButtonsWrap>
              {showButtonSumDay && (
                <WidgetMethodButton
                  color={color}
                  calcMethod={calcMethod}
                  onClick={onMethodClick}
                  disabled={!selected}
                />
              )}

              {showButtonGear && (
                <WidgetGearButton
                  color={color}
                  href={`/${ROUTES.DEFAULT_PROJECT}/funnel?companyId=${currentCompany.id}&edit=1`}
                />
              )}

              {showScaleButton && (
                <WidgetScaleButton
                  color={color}
                  onClick={handleScale}
                  currentValue={widgetSettings?.isScaleChecked ? 'log' : 'linear'}
                  disabled={!selected}
                />
              )}
            </ButtonsWrap>
          )}
        </BodyRow>
      </Content>

      <CrSelectPopover
        anchorEl={crAnchorEl}
        open={Boolean(crAnchorEl)}
        onClose={handleCloseCr}
        options={crOptions}
        onConfirm={(a, b) => onCrConfirm?.(a, b)}
      />
    </Root>
  );
};

export default PropertyWidget;
